import {
  CURRENCIES,
  discountedPrices,
  savedAmount,
  type Currency,
  type PackageItem,
  type PriceMap,
  type Product,
} from "@nh/shared";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El precio que va a cobrar el paquete, calculado ANTES de guardar
 *
 * El contrato es tajante: el paquete no tiene campo de precio. Se
 * escribe el DESCUENTO y el servidor deriva `listPrices` y `prices`
 * sumando los productos. Eso está bien —una sola fuente no se
 * desincroniza— pero deja a quien arma el paquete tecleando un 20 % a
 * ciegas y descubriendo lo que cobra después de guardar.
 *
 * Este archivo hace la MISMA cuenta que hará el servidor, con las mismas
 * funciones del contrato (`discountedPrices` → `applyDiscount`, que
 * redondea hacia abajo al centavo), para poder enseñarla mientras se
 * edita. No la sustituye: lo que se guarda sigue siendo el descuento, y
 * el precio que manda es el que devuelva el servidor. Es una vista
 * previa, y la pantalla lo dice.
 *
 * Por eso se calcula aquí y no dentro de un componente: es aritmética de
 * dinero, se prueba sola, y ningún componente divide entre 100 en ningún
 * sitio — el formato es cosa de `formatPrice`.
 * ═══════════════════════════════════════════════════════════════════
 */

/** Una línea del paquete ya resuelta contra el catálogo. */
export interface PackageLine {
  productId: string;
  quantity: number;
  /** `undefined` si el catálogo cargado no tiene ese producto. */
  product: Product | undefined;
}

export interface PackagePreview {
  lines: PackageLine[];
  /** Cuántas unidades en total, sumando cantidades. */
  units: number;
  /**
   * Ids referenciados que el catálogo no conoce. Mientras haya alguno,
   * la cuenta está incompleta y la pantalla tiene que decirlo: un total
   * al que le falta un producto se lee como un total.
   */
  missingProductIds: string[];
  /** La suma de sus productos a precio normal, por moneda. */
  listPrices: PriceMap;
  /** Lo que se cobraría: lista menos el descuento. */
  prices: PriceMap;
  /** Lo que se ahorra, por moneda. */
  savings: PriceMap;
  /**
   * Monedas en las que ALGUNOS productos tienen precio y otros no.
   *
   * No entran en el total. Sumar solo los que tienen precio daría un
   * paquete más barato de lo que cuesta, y esa cifra iría a la tienda.
   */
  incompleteCurrencies: Currency[];
  /** Los VP del paquete: la suma de los de sus productos. */
  volumePoints: number;
  /** `true` si algún producto del paquete no declara VP. */
  volumePointsIncomplete: boolean;
}

/** El catálogo indexado por id, que es como lo consultan las líneas. */
export function indexProducts(products: readonly Product[]): Map<string, Product> {
  return new Map(products.map((product) => [product.id, product]));
}

/**
 * La vista previa del paquete.
 *
 * `items` viene del formulario ya con cantidades numéricas; las filas a
 * medio llenar —sin producto elegido— se descartan antes de llamar
 * aquí, porque una fila vacía no cambia lo que cuesta el paquete.
 */
export function computePackagePreview(
  items: readonly PackageItem[],
  catalog: ReadonlyMap<string, Product>,
  discountPercent: number,
): PackagePreview {
  const lines: PackageLine[] = items.map((item) => ({
    productId: item.productId,
    quantity: item.quantity,
    product: catalog.get(item.productId),
  }));

  const missingProductIds = lines
    .filter((line) => !line.product)
    .map((line) => line.productId);

  const units = lines.reduce((total, line) => total + line.quantity, 0);

  const listPrices: PriceMap = {};
  const incompleteCurrencies: Currency[] = [];

  for (const currency of CURRENCIES) {
    /*
     * Una moneda entra al total solo si TODAS las líneas tienen precio en
     * ella. Tres estados, y los tres significan cosas distintas:
     *
     *   ninguna la tiene  → el paquete no se vende en esa moneda. No es
     *                       un error y no se enseña.
     *   todas la tienen   → se suma.
     *   unas sí, otras no → incompleta. NO se suma, y se avisa: un total
     *                       al que le falta un producto es un precio
     *                       equivocado que nadie va a notar.
     */
    let withPrice = 0;
    let subtotal = 0;

    for (const line of lines) {
      const cents = line.product?.prices[currency];
      if (cents === undefined) continue;
      withPrice += 1;
      subtotal += cents * line.quantity;
    }

    if (withPrice === 0) continue;
    if (withPrice < lines.length) {
      incompleteCurrencies.push(currency);
      continue;
    }
    listPrices[currency] = subtotal;
  }

  const prices = discountedPrices(listPrices, discountPercent);

  const savings: PriceMap = {};
  for (const currency of CURRENCIES) {
    const saved = savedAmount(listPrices, prices, currency);
    if (saved !== null) savings[currency] = saved;
  }

  /*
   * Los VP se suman igual que el precio, y se guardan ya resueltos: el
   * día que un producto cambie de puntos, los paquetes ya vendidos no
   * cambian de valor retroactivamente (contrato, `volumePoints`).
   *
   * Un producto sin VP declarado cuenta como 0 y se AVISA. `undefined`
   * no es cero: es "nadie lo ha decidido todavía", y un paquete que
   * promete 0 VP porque falta capturar un dato es un paquete que no
   * califica a nadie.
   */
  let volumePoints = 0;
  let volumePointsIncomplete = false;
  for (const line of lines) {
    const points = line.product?.volumePoints;
    if (points === undefined) {
      volumePointsIncomplete = true;
      continue;
    }
    volumePoints += points * line.quantity;
  }

  return {
    lines,
    units,
    missingProductIds,
    listPrices,
    prices,
    savings,
    incompleteCurrencies,
    volumePoints,
    volumePointsIncomplete,
  };
}

/** Las monedas que la vista previa sí puede cobrar, en orden del contrato. */
export function pricedCurrencies(preview: PackagePreview): Currency[] {
  return CURRENCIES.filter((currency) => preview.prices[currency] !== undefined);
}
